import type { UpgradeSystem } from '@full-elevator/sim';
import { COLORS, LIFT_PARTS } from '../theme.js';
import type { LiftLook } from '../view/liftParts.js';

/**
 * A picture of every part in the garage, one per tier.
 *
 * Visual progression backs up the mechanical kind (the plan update asks for
 * both): each tier is visibly bigger, heavier and better finished than the one
 * before, so "what did I buy" has an answer you can see before you read it.
 * These are the parts the player owns, never alternatives to buy — a part
 * only ever looks the way its tier performs, which is what keeps this from
 * being a skin.
 *
 * Drawn on a 120×100 box in the Warm Miniature hand: brown ink outlines, warm
 * steel, brass. No floor hue appears anywhere, so the same look can be carried
 * onto the lift in play without a part ever reading as a destination.
 */
const css = (colour: number): string => `#${colour.toString(16).padStart(6, '0')}`;

export const PART_INK = css(COLORS.ink);
/** The lift's own colours, shared with play; the last three are the garage's alone. */
export const PART = {
  steel: css(LIFT_PARTS.steel),
  steelDark: css(LIFT_PARTS.steelDark),
  iron: css(LIFT_PARTS.iron),
  brass: css(LIFT_PARTS.brass),
  glow: css(LIFT_PARTS.glow),
  cream: css(COLORS.cabin),
  // Garage only: these sit too close to floor 2 to be drawn in the shaft.
  brassDark: '#8a6a2e',
  panel: '#efe3c8',
  wood: '#e3c9a0',
} as const;

const INK = `stroke="${PART_INK}"`;

/** A toothed gear as a path: `teeth` square teeth on a disc. */
function gear(cx: number, cy: number, r: number, teeth: number): string {
  const points: string[] = [];
  const inner = r - 4;
  for (let i = 0; i < teeth * 4; i += 1) {
    const a = (Math.PI * 2 * i) / (teeth * 4);
    const radius = i % 4 < 2 ? r : inner;
    points.push(
      `${(cx + radius * Math.cos(a)).toFixed(1)} ${(cy + radius * Math.sin(a)).toFixed(1)}`,
    );
  }
  return `M${points.join(' L')} Z`;
}

/** A pulley: rim, groove, spokes and hub. */
function pulley(cx: number, cy: number, r: number, spokes: number): string {
  let s = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#d9d2c6" ${INK} stroke-width="2.5"/>`;
  s += `<circle cx="${cx}" cy="${cy}" r="${r - 4}" fill="none" ${INK} stroke-width="1" opacity="0.5"/>`;
  for (let i = 0; i < spokes; i += 1) {
    const a = (Math.PI * 2 * i) / spokes;
    s += `<path d="M${cx} ${cy} L${(cx + (r - 4) * Math.cos(a)).toFixed(1)} ${(cy + (r - 4) * Math.sin(a)).toFixed(1)}" ${INK} stroke-width="1.5"/>`;
  }
  return `${s}<circle cx="${cx}" cy="${cy}" r="${Math.max(3, r / 4)}" fill="${PART_INK}"/>`;
}

const MOTOR = [
  // Standard: a plain box with one small pulley.
  `<rect x="22" y="78" width="76" height="8" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2.5"/>
   <rect x="30" y="46" width="42" height="32" rx="6" fill="${PART.steel}" ${INK} stroke-width="2.5"/>
   <path d="M38 56 h26 M38 62 h26 M38 68 h26" ${INK} stroke-width="1.5" opacity="0.45"/>
   <rect x="72" y="58" width="6" height="6" fill="${PART_INK}"/>
   ${pulley(86, 61, 11, 4)}
   <path d="M97 61 V100" ${INK} stroke-width="2"/>`,
  // Geared: a heavier housing and a brass gear train.
  `<rect x="12" y="80" width="96" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2.5"/>
   <rect x="20" y="38" width="50" height="42" rx="7" fill="#9c8f7e" ${INK} stroke-width="2.5"/>
   <rect x="28" y="50" width="26" height="12" rx="2" fill="${PART.cream}" ${INK} stroke-width="1.2"/>
   <path d="M32 56 h18" ${INK} stroke-width="1.5" opacity="0.6"/>
   <circle cx="25" cy="43" r="2" fill="${PART.brass}"/><circle cx="65" cy="43" r="2" fill="${PART.brass}"/>
   <circle cx="25" cy="75" r="2" fill="${PART.brass}"/><circle cx="65" cy="75" r="2" fill="${PART.brass}"/>
   <path d="${gear(66, 66, 9, 7)}" fill="${PART.brass}" ${INK} stroke-width="1.8"/>
   <circle cx="66" cy="66" r="3" fill="${PART.brassDark}"/>
   <path d="${gear(86, 52, 18, 10)}" fill="${PART.brass}" ${INK} stroke-width="2.2"/>
   <circle cx="86" cy="52" r="7" fill="${PART.brassDark}" ${INK} stroke-width="1.5"/>
   <path d="M100 56 V100" ${INK} stroke-width="2.2"/>`,
  // High-speed: an iron capsule, brass speed stripes, twin pulleys and a lit lamp.
  `<rect x="6" y="82" width="108" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2.5"/>
   <rect x="12" y="30" width="62" height="52" rx="18" fill="${PART.iron}" ${INK} stroke-width="2.5"/>
   <path d="M30 70 l12 -30 h7 l-12 30 z M44 70 l12 -30 h7 l-12 30 z" fill="${PART.brass}"/>
   <path d="M17 46 v24 M21 44 v28" stroke="${PART.steel}" stroke-width="2" opacity="0.7"/>
   <circle cx="62" cy="40" r="7" fill="${PART.glow}" opacity="0.35"/>
   <circle cx="62" cy="40" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   ${pulley(92, 44, 18, 6)}
   ${pulley(92, 74, 8, 3)}
   <path d="M110 44 V100 M74 44 h0" ${INK} stroke-width="2.2"/>
   <path d="M112 26 h6 M110 32 h8 M112 38 h6" ${INK} stroke-width="1.5" opacity="0.5" stroke-linecap="round"/>`,
  // Precision (ADR-0033): the capsule in a brass collar, two dials and a bigger lamp.
  `<rect x="4" y="82" width="112" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2.5"/>
   <rect x="10" y="28" width="66" height="54" rx="19" fill="${PART.iron}" ${INK} stroke-width="2.5"/>
   <rect x="34" y="26" width="12" height="58" rx="3" fill="${PART.brass}" ${INK} stroke-width="2"/>
   <circle cx="22" cy="48" r="6" fill="${PART.cream}" ${INK} stroke-width="1.5"/>
   <path d="M22 48 l3 -3" ${INK} stroke-width="1.5" stroke-linecap="round"/>
   <circle cx="22" cy="66" r="6" fill="${PART.cream}" ${INK} stroke-width="1.5"/>
   <path d="M22 66 l-2 -4" ${INK} stroke-width="1.5" stroke-linecap="round"/>
   <circle cx="62" cy="40" r="9" fill="${PART.glow}" opacity="0.35"/>
   <circle cx="62" cy="40" r="4.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   ${pulley(94, 44, 19, 8)}
   ${pulley(94, 75, 9, 4)}
   <path d="M113 44 V100" ${INK} stroke-width="2.4"/>
   <path d="M108 20 h8 M106 27 h10 M108 34 h8" ${INK} stroke-width="1.6" opacity="0.5" stroke-linecap="round"/>`,
  // Turbo: a finned housing with a turbine on its face and twin lamps.
  `<rect x="2" y="82" width="116" height="10" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2.5"/>
   <rect x="8" y="24" width="70" height="58" rx="16" fill="${PART.iron}" ${INK} stroke-width="2.5"/>
   <path d="M16 30 v46 M22 28 v50 M28 28 v50" stroke="${PART.steel}" stroke-width="2.4" opacity="0.8"/>
   <circle cx="52" cy="53" r="17" fill="${PART.steelDark}" ${INK} stroke-width="2"/>
   <path d="M52 53 L52 38 M52 53 L66 60 M52 53 L39 61 M52 53 L64 43 M52 53 L41 44" stroke="${PART.brass}" stroke-width="3" stroke-linecap="round"/>
   <circle cx="52" cy="53" r="4" fill="${PART.brass}" ${INK} stroke-width="1.2"/>
   <circle cx="38" cy="30" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   <circle cx="66" cy="30" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   ${pulley(96, 44, 19, 10)}
   <path d="M115 44 V100" ${INK} stroke-width="2.6"/>
   <path d="M104 16 h12 M100 23 h16 M104 30 h12" ${INK} stroke-width="1.8" opacity="0.55" stroke-linecap="round"/>`,
  // Linear: a magnetic coil in lit rings, the sheave riding on light.
  `<ellipse cx="60" cy="88" rx="56" ry="6" fill="${PART.glow}" opacity="0.3"/>
   <rect x="2" y="82" width="116" height="9" rx="2" fill="${PART.iron}" ${INK} stroke-width="2.5"/>
   <rect x="6" y="34" width="78" height="40" rx="20" fill="${PART.iron}" ${INK} stroke-width="2.5"/>
   <path d="M22 36 v36 M36 34 v40 M50 34 v40 M64 34 v40" stroke="${PART.glow}" stroke-width="4"/>
   <path d="M22 36 v36 M36 34 v40 M50 34 v40 M64 34 v40" stroke="${PART_INK}" stroke-width="1" opacity="0.4"/>
   <circle cx="96" cy="46" r="24" fill="${PART.glow}" opacity="0.25"/>
   ${pulley(96, 46, 19, 12)}
   <path d="M115 46 V100" stroke="${PART.iron}" stroke-width="3.2"/>
   <path d="M2 22 h20 M6 28 h26 M2 34 h14" stroke="${PART.brass}" stroke-width="2" stroke-linecap="round"/>`,
];

/** Seats as dots on a cabin floor: two abreast, three once a car seats more than six. */
function seats(count: number, x: number, y: number, w: number, h: number): string {
  const across = count > 6 ? 3 : 2;
  const rows = Math.ceil(count / across);
  const r = count > 8 ? 4.2 : count > 6 ? 5 : 6;
  let s = '';
  for (let i = 0; i < count; i += 1) {
    const cx = x + (w * ((i % across) * 2 + 1)) / (across * 2);
    const cy = y + (h * (Math.floor(i / across) + 0.5)) / rows;
    s += `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r}" fill="#cdb795" ${INK} stroke-width="1.2"/>`;
  }
  return s;
}

/** The cabin at each tier, drawn with as many seats as that tier holds in its building's lift. */
const CABIN: readonly ((count: number) => string)[] = [
  // The stock car: the plain box.
  (count) => `<path d="M60 0 V22" ${INK} stroke-width="2"/>
   <path d="M44 24 L60 18 L76 24 Z" fill="${PART_INK}"/>
   <rect x="32" y="23" width="56" height="70" rx="8" fill="${PART.panel}" ${INK} stroke-width="3"/>
   ${seats(count, 36, 30, 48, 58)}`,
  // The middle car: wider, panelled in wood, a brass rail.
  (count) => `<path d="M60 0 V18" ${INK} stroke-width="2"/>
   <path d="M40 20 L60 13 L80 20 Z" fill="${PART_INK}"/>
   <rect x="25" y="19" width="70" height="76" rx="9" fill="${PART.wood}" ${INK} stroke-width="3"/>
   <path d="M42 24 V90 M60 24 V90 M78 24 V90" stroke="#b58a5c" stroke-width="1.5" opacity="0.6"/>
   <path d="M30 60 H90" stroke="${PART.brass}" stroke-width="3" stroke-linecap="round"/>
   ${seats(count, 30, 26, 60, 64)}`,
  // The big car: brass-trimmed, a mirror and a lamp.
  (count) => `<path d="M60 0 V12" ${INK} stroke-width="2"/>
   <path d="M36 14 L60 6 L84 14 Z" fill="${PART_INK}"/>
   <rect x="15" y="13" width="90" height="84" rx="10" fill="#fff1d6" ${INK} stroke-width="3"/>
   <rect x="20" y="18" width="80" height="74" rx="7" fill="none" stroke="${PART.brass}" stroke-width="3"/>
   <rect x="44" y="23" width="32" height="12" rx="3" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <circle cx="60" cy="40" r="9" fill="${PART.glow}" opacity="0.4"/>
   <path d="M26 64 H94" stroke="${PART.brass}" stroke-width="3" stroke-linecap="round"/>
   ${seats(count, 26, 40, 68, 50)}`,
  // Tiers 3–5 (ADR-0033): the big car, finished better each time.
  (count) => bigCar(count, 3),
  (count) => bigCar(count, 4),
  (count) => bigCar(count, 5),
];

/**
 * The best cars: the big car's brass trim, then a double rail and a mirror
 * wall (3), a chandelier and a gilded frame (4), and a lit aura (5).
 */
function bigCar(count: number, tier: number): string {
  const aura =
    tier >= 5
      ? `<rect x="6" y="4" width="108" height="96" rx="14" fill="${PART.glow}" opacity="0.28"/>`
      : '';
  const frame = tier >= 4 ? PART.brassDark : PART.brass;
  const chandelier =
    tier >= 4
      ? `<circle cx="60" cy="31" r="10" fill="${PART.glow}" opacity="0.45"/>
         <path d="M52 27 Q60 36 68 27" fill="none" stroke="${PART.brass}" stroke-width="2"/>
         <circle cx="52" cy="28" r="2" fill="${PART.glow}" ${INK} stroke-width="0.8"/>
         <circle cx="60" cy="32" r="2.4" fill="${PART.glow}" ${INK} stroke-width="0.8"/>
         <circle cx="68" cy="28" r="2" fill="${PART.glow}" ${INK} stroke-width="0.8"/>`
      : `<circle cx="60" cy="31" r="8" fill="${PART.glow}" opacity="0.4"/>`;
  return `${aura}
   <path d="M60 0 V10" ${INK} stroke-width="2"/>
   <path d="M34 12 L60 3 L86 12 Z" fill="${tier >= 4 ? PART.brass : PART_INK}" ${INK} stroke-width="1.2"/>
   <rect x="12" y="11" width="96" height="87" rx="11" fill="#fff1d6" ${INK} stroke-width="3"/>
   <rect x="17" y="16" width="86" height="77" rx="8" fill="none" stroke="${frame}" stroke-width="${tier >= 4 ? 4 : 3}"/>
   <rect x="22" y="21" width="76" height="67" rx="6" fill="none" stroke="${PART.brass}" stroke-width="1.5"/>
   <rect x="36" y="20" width="48" height="8" rx="2" fill="#d9e4e6" ${INK} stroke-width="1"/>
   ${chandelier}
   <path d="M24 62 H96 M24 67 H96" stroke="${PART.brass}" stroke-width="2.2" stroke-linecap="round"/>
   ${tier >= 5 ? `<path d="M60 -4 l2 5 5 .5 -4 3 1.2 5 -4.2 -2.6 -4.2 2.6 1.2 -5 -4 -3 5 -.5z" fill="${PART.glow}" ${INK} stroke-width="0.8"/>` : ''}
   ${seats(count, 24, 38, 72, 50)}`;
}

const DOORS = [
  // Manual: a folding scissor gate you pull across by hand.
  `<rect x="20" y="10" width="80" height="86" rx="4" fill="#e6d6b8" ${INK} stroke-width="3"/>
   <rect x="26" y="16" width="68" height="74" fill="${PART.cream}" ${INK} stroke-width="1.5"/>
   <path d="M26 16 L43 53 L26 90 M43 16 L26 53 L43 90 M43 16 L60 53 L43 90 M60 16 L43 53 L60 90 M60 16 L77 53 L60 90 M77 16 L60 53 L77 90 M77 16 L94 53 L77 90 M94 16 L77 53 L94 90" fill="none" stroke="${PART.steelDark}" stroke-width="2"/>
   <path d="M26 16 V90 M43 16 V90 M60 16 V90 M77 16 V90 M94 16 V90" stroke="${PART_INK}" stroke-width="2"/>
   <circle cx="89" cy="53" r="4" fill="${PART.brass}" ${INK} stroke-width="1.2"/>`,
  // Powered: two sliding panels, windowed, with their own motor box above.
  `<rect x="46" y="1" width="28" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2"/>
   <rect x="20" y="10" width="80" height="86" rx="4" fill="#e6d6b8" ${INK} stroke-width="3"/>
   <rect x="25" y="15" width="34" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="34" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="36" y="24" width="12" height="30" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <rect x="72" y="24" width="12" height="30" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <path d="M33 74 l-5 4 l5 4 M87 74 l5 4 l-5 4" fill="none" ${INK} stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`,
  // Express: brass-framed panels with chevrons and a lit ready lamp.
  `<circle cx="60" cy="5" r="7" fill="${PART.glow}" opacity="0.35"/>
   <circle cx="60" cy="5" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   <rect x="18" y="10" width="84" height="88" rx="5" fill="${PART.brass}" ${INK} stroke-width="3"/>
   <rect x="24" y="16" width="35" height="76" rx="2" fill="${PART.panel}" ${INK} stroke-width="2"/>
   <rect x="61" y="16" width="35" height="76" rx="2" fill="${PART.panel}" ${INK} stroke-width="2"/>
   <path d="M55 16 V92 M65 16 V92" stroke="${PART.brass}" stroke-width="3"/>
   <path d="M46 38 l-10 10 l10 10 M74 38 l10 10 l-10 10" fill="none" stroke="${PART.brassDark}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
   <path d="M46 62 l-10 10 l10 10 M74 62 l10 10 l-10 10" fill="none" stroke="${PART.brassDark}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.5"/>`,
];

/** The frame every door picture stands in. */
const FRAME = `<rect x="20" y="10" width="80" height="86" rx="4" fill="#e6d6b8" ${INK} stroke-width="3"/>`;

/** The Grand Hotel's doors (ADR-0027): a brass gate, brass doors, then sunburst doors. */
const BRASS_DOORS = [
  `${FRAME}
   <rect x="26" y="16" width="68" height="74" fill="${PART.cream}" ${INK} stroke-width="1.5"/>
   <path d="M26 16 L43 53 L26 90 M43 16 L26 53 L43 90 M43 16 L60 53 L43 90 M60 16 L43 53 L60 90 M60 16 L77 53 L60 90 M77 16 L60 53 L77 90 M77 16 L94 53 L77 90 M94 16 L77 53 L94 90" fill="none" stroke="${PART.brass}" stroke-width="2.4"/>
   <path d="M26 16 V90 M43 16 V90 M60 16 V90 M77 16 V90 M94 16 V90" stroke="${PART.brassDark}" stroke-width="2"/>
   <circle cx="89" cy="53" r="4" fill="${PART.brass}" ${INK} stroke-width="1.2"/>`,
  `${FRAME}
   <rect x="25" y="15" width="34" height="76" rx="2" fill="${PART.brass}" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="34" height="76" rx="2" fill="${PART.brass}" ${INK} stroke-width="2"/>
   <rect x="29" y="19" width="26" height="68" rx="1" fill="none" stroke="${PART.brassDark}" stroke-width="1.5"/>
   <rect x="65" y="19" width="26" height="68" rx="1" fill="none" stroke="${PART.brassDark}" stroke-width="1.5"/>
   <path d="M29 58 H55 M65 58 H91" stroke="${PART.brassDark}" stroke-width="1.5"/>`,
  `<circle cx="60" cy="5" r="7" fill="${PART.glow}" opacity="0.35"/>
   <circle cx="60" cy="5" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   <rect x="18" y="10" width="84" height="88" rx="5" fill="${PART.brassDark}" ${INK} stroke-width="3"/>
   <rect x="24" y="16" width="35" height="76" rx="2" fill="${PART.brass}" ${INK} stroke-width="2"/>
   <rect x="61" y="16" width="35" height="76" rx="2" fill="${PART.brass}" ${INK} stroke-width="2"/>
   <path d="M59 50 L26 18 M59 50 L37 17 M59 50 L48 17 M59 50 L26 32 M61 50 L94 18 M61 50 L83 17 M61 50 L72 17 M61 50 L94 32" stroke="${PART.panel}" stroke-width="2" stroke-linecap="round"/>
   <path d="M24 70 H59 M61 70 H96" stroke="${PART.brassDark}" stroke-width="2"/>`,
];

/** Hazard stripes down a door's meeting edge. */
function hazard(x: number, y: number, w: number, h: number): string {
  let stripes = '';
  for (let k = y; k < y + h - 2; k += 8) {
    stripes += `M${x} ${k + 6} L${x + w} ${k}`;
  }
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${PART.glow}"/>
   <path d="${stripes}" stroke="${PART_INK}" stroke-width="2.6"/>`;
}

/** The Transit Centre's doors (ADR-0027): wide and hazard-edged, then powered, then glass above. */
const WIDE_DOORS = [
  `<rect x="10" y="10" width="100" height="86" rx="4" fill="#e6d6b8" ${INK} stroke-width="3"/>
   <rect x="15" y="15" width="44" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="44" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   ${hazard(52, 16, 7, 74)}${hazard(61, 16, 7, 74)}`,
  `<rect x="46" y="1" width="28" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2"/>
   <rect x="10" y="10" width="100" height="86" rx="4" fill="#e6d6b8" ${INK} stroke-width="3"/>
   <rect x="15" y="15" width="44" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="44" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="24" y="24" width="20" height="22" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <rect x="76" y="24" width="20" height="22" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   ${hazard(52, 16, 7, 74)}${hazard(61, 16, 7, 74)}`,
  `<circle cx="60" cy="5" r="7" fill="${PART.glow}" opacity="0.35"/>
   <circle cx="60" cy="5" r="3.5" fill="${PART.glow}" ${INK} stroke-width="1"/>
   <rect x="8" y="10" width="104" height="88" rx="5" fill="${PART.steelDark}" ${INK} stroke-width="3"/>
   <rect x="14" y="16" width="45" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="61" y="16" width="45" height="76" rx="2" fill="#d9cbb4" ${INK} stroke-width="2"/>
   <rect x="18" y="20" width="32" height="34" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <rect x="70" y="20" width="32" height="34" rx="2" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   ${hazard(52, 17, 7, 74)}${hazard(61, 17, 7, 74)}`,
];

const LIGHT = '#7fd0e6';

/** Skyline Tower's doors (ADR-0027): framed glass, then slimmer frames, then frameless with a light. */
const GLASS_DOORS = [
  `${FRAME}
   <rect x="25" y="15" width="34" height="76" rx="2" fill="${PART.steel}" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="34" height="76" rx="2" fill="${PART.steel}" ${INK} stroke-width="2"/>
   <rect x="31" y="21" width="22" height="64" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <rect x="67" y="21" width="22" height="64" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <path d="M34 40 L46 24 M70 40 L82 24" stroke="${PART.cream}" stroke-width="2.5" stroke-linecap="round"/>`,
  `<rect x="46" y="1" width="28" height="9" rx="2" fill="${PART.steelDark}" ${INK} stroke-width="2"/>
   ${FRAME}
   <rect x="25" y="15" width="34" height="76" rx="2" fill="#d9e4e6" ${INK} stroke-width="2"/>
   <rect x="61" y="15" width="34" height="76" rx="2" fill="#d9e4e6" ${INK} stroke-width="2"/>
   <path d="M30 44 L48 20 M66 44 L84 20" stroke="${PART.cream}" stroke-width="3" stroke-linecap="round"/>
   <path d="M33 74 l-5 4 l5 4 M87 74 l5 4 l-5 4" fill="none" ${INK} stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>`,
  `<circle cx="60" cy="5" r="7" fill="${LIGHT}" opacity="0.35"/>
   <circle cx="60" cy="5" r="3.5" fill="${LIGHT}" ${INK} stroke-width="1"/>
   <rect x="18" y="10" width="84" height="88" rx="5" fill="${PART.iron}" ${INK} stroke-width="3"/>
   <rect x="23" y="15" width="36" height="78" rx="1" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <rect x="61" y="15" width="36" height="78" rx="1" fill="#d9e4e6" ${INK} stroke-width="1.2"/>
   <path d="M57 16 V92 M63 16 V92" stroke="${LIGHT}" stroke-width="3"/>
   <path d="M28 46 L50 18 M66 46 L88 18" stroke="${PART.cream}" stroke-width="3" stroke-linecap="round"/>`,
];

/**
 * Tiers 3–5 of any building's doors (ADR-0033): its best doors, finished
 * further — brass rivets along the head (3), a lit lintel (4), a lit surround
 * and a star lamp (5). The building's finish stays the building's.
 */
function finished(best: string, tier: number): string {
  const rivets = [26, 40, 54, 66, 80, 94]
    .map(
      (x) => `<circle cx="${x}" cy="13" r="1.8" fill="${PART.brass}" ${INK} stroke-width="0.8"/>`,
    )
    .join('');
  const lintel =
    tier >= 4
      ? `<rect x="16" y="2" width="88" height="6" rx="3" fill="${PART.glow}" ${INK} stroke-width="1.2"/>`
      : '';
  const surround =
    tier >= 5
      ? `<rect x="10" y="4" width="100" height="98" rx="9" fill="none" stroke="${PART.glow}" stroke-width="5" opacity="0.6"/>`
      : '';
  return `${surround}${best}${lintel}${rivets}`;
}

/** A look's six tiers: its three own pictures, then its best one, finished further. */
function sixTiers(own: readonly string[]): readonly string[] {
  const best = own[own.length - 1] as string;
  return [...own, finished(best, 3), finished(best, 4), finished(best, 5)];
}

/** Every building's doors, by how its lift is finished. */
const DOORS_BY_LOOK: Readonly<Record<LiftLook, readonly string[]>> = {
  steel: sixTiers(DOORS),
  brass: sixTiers(BRASS_DOORS),
  wide: sixTiers(WIDE_DOORS),
  glass: sixTiers(GLASS_DOORS),
};

/** A rope from the sheave to the car's hitch, with its strands' lay. */
function rope(x: number, width: number, colour: string, lay: string, layEvery: number): string {
  let marks = '';
  for (let y = 36; y < 84; y += layEvery)
    marks += `M${x - width / 2} ${y + 2.4} L${x + width / 2} ${y}`;
  return `<path d="M${x} 32 V86" stroke="${colour}" stroke-width="${width}"/>
   <path d="M${x} 32 V86" ${INK} stroke-width="${width + 1.6}" opacity="0.18"/>
   <path d="${marks}" stroke="${lay}" stroke-width="1.2" opacity="0.8"/>`;
}

/**
 * The cable at each tier (ADR-0033): the ropes from the sheave to the car,
 * thicker, more of them and better made each time — steel, then compacted
 * strands, aramid with its yellow tracer, carbon, and a carbon belt.
 */
const CABLE: readonly string[] = [
  [rope(54, 2.6, PART.steel, PART.steelDark, 7), rope(66, 2.6, PART.steel, PART.steelDark, 7)],
  [rope(53, 4, PART.steel, PART_INK, 6), rope(67, 4, PART.steel, PART_INK, 6)],
  [
    rope(48, 4.4, PART.steelDark, PART_INK, 5),
    rope(60, 4.4, PART.steelDark, PART_INK, 5),
    rope(72, 4.4, PART.steelDark, PART_INK, 5),
  ],
  [rope(53, 5, PART.iron, PART.glow, 5), rope(67, 5, PART.iron, PART.glow, 5)],
  [
    rope(48, 5, PART.iron, PART.steel, 4),
    rope(60, 5, PART.iron, PART.steel, 4),
    rope(72, 5, PART.iron, PART.steel, 4),
  ],
  [
    `<rect x="49" y="32" width="22" height="54" rx="2" fill="${PART.iron}" ${INK} stroke-width="1.5"/>
    <path d="M54 32 V86 M60 32 V86 M66 32 V86" stroke="${PART.steel}" stroke-width="1" opacity="0.5"/>
    <rect x="46" y="48" width="28" height="5" rx="1.5" fill="${PART.brass}" ${INK} stroke-width="1"/>
    <rect x="46" y="66" width="28" height="5" rx="1.5" fill="${PART.brass}" ${INK} stroke-width="1"/>`,
  ],
].map((ropes, tier) => {
  const glow =
    tier >= 3
      ? `<circle cx="60" cy="20" r="${18 + tier * 2}" fill="${PART.glow}" opacity="${0.12 + tier * 0.05}"/>`
      : '';
  const lines =
    tier >= 2
      ? `<path d="M${16 + tier} 50 h${8 + tier * 3} M${12 + tier} 58 h${12 + tier * 3} M${16 + tier} 66 h${8 + tier * 3}" ${INK} stroke-width="1.6" opacity="0.4" stroke-linecap="round"/>`
      : '';
  return `${glow}
   <rect x="22" y="2" width="76" height="6" rx="2" fill="${PART.iron}" ${INK} stroke-width="1.5"/>
   ${ropes.join('')}
   ${pulley(60, 20, 15, 4 + tier)}
   <rect x="40" y="84" width="40" height="12" rx="3" fill="${PART.steelDark}" ${INK} stroke-width="2"/>
   <path d="M50 90 h20" ${INK} stroke-width="1.5" opacity="0.5"/>
   ${lines}`;
});

/**
 * The part at a tier, as an inline SVG element, in its building's lift
 * (ADR-0027): the doors in the building's finish, the cabin with as many seats
 * as that tier holds there.
 */
export function partSvg(
  system: UpgradeSystem,
  tier: number,
  className = 'part-art',
  lift: { look: LiftLook; seats: number } = { look: 'steel', seats: [4, 5, 6][tier] ?? 6 },
): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 120 100');
  svg.setAttribute('class', className);
  svg.setAttribute('aria-hidden', 'true');
  const at = <T>(tiers: readonly T[]): T | undefined =>
    tiers[Math.max(0, Math.min(tier, tiers.length - 1))];
  const art: Record<UpgradeSystem, () => string> = {
    motor: () => at(MOTOR) ?? '',
    cabin: () => at(CABIN)?.(lift.seats) ?? '',
    doors: () => at(DOORS_BY_LOOK[lift.look]) ?? '',
    cable: () => at(CABLE) ?? '',
  };
  svg.innerHTML = art[system]();
  return svg;
}

/** Every door picture, for the colour test. */
export const DOOR_ART = Object.values(DOORS_BY_LOOK).flat().join('\n');

/** Every cable picture, for the colour test. */
export const CABLE_ART = CABLE.join('\n');
